/**
 * Staff "view as portal user".
 *
 * PazNetworks staff carry the `is_paznetworks_staff()` RLS bypass, so a staff
 * session that simply opened the portal would see every customer's data —
 * the opposite of "what does this customer see". Instead, a staff member
 * must pick a portal user; the portal then loads THAT user's profile and
 * account links and scopes every query to them.
 *
 *   • Only staff JWT roles read `?as_user=<users.id>` (VIEW_AS_QUERY_PARAM).
 *     Non-staff sessions ignore the parameter entirely.
 *   • The choice persists in sessionStorage (VIEW_AS_STORAGE_KEY) so it
 *     survives in-app navigation and reloads within the tab.
 *   • The impersonation is recorded once per view-as session through
 *     `record_staff_view_as()` (see AccountContext, which knows the primary
 *     account id). Failures are surfaced in the banner.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isStaffRole, VIEW_AS_QUERY_PARAM, VIEW_AS_STORAGE_KEY } from '@pazportal/types';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

export const STAFF_VIEW_AS_PATH = '/staff/view-as';

/** sessionStorage key: the last users.id for which record_staff_view_as() succeeded. */
export const VIEW_AS_RECORDED_KEY = `${VIEW_AS_STORAGE_KEY}:recorded`;

export interface ViewedUser {
  id: string;
  email: string;
  full_name: string | null;
}

export type AuditRecordState =
  | { status: 'idle' }
  | { status: 'recorded' }
  | { status: 'failed'; message: string };

export type ViewAsNotice = 'user_not_found' | null;

interface ViewAsState {
  /** JWT role is one of the five PazNetworks staff roles. */
  isStaff: boolean;
  /** users.id being viewed. Always null for non-staff sessions. */
  viewAsUserId: string | null;
  /** Profile of the viewed user (null while loading or when not found). */
  viewedUser: ViewedUser | null;
  viewedUserLoading: boolean;
  /** Start viewing as `userId` (staff only). */
  setViewAsUser: (userId: string) => void;
  /** Leave view-as mode and return to the picker. */
  exitViewAs: () => void;
  auditRecord: AuditRecordState;
  setAuditRecord: (state: AuditRecordState) => void;
  notice: ViewAsNotice;
  clearNotice: () => void;
}

const ViewAsContext = createContext<ViewAsState | undefined>(undefined);

/** The `?as_user=` value currently in the address bar, if any. */
export function readViewAsParamFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const value = new URLSearchParams(window.location.search).get(VIEW_AS_QUERY_PARAM);
  return value ? value.trim() || null : null;
}

/**
 * Initial target: the URL parameter wins over the stored value so a fresh
 * admin link always lands on the intended user. This runs synchronously in
 * the state initialiser so the first render already knows the target and
 * the layout never bounces a staff member to the picker mid-navigation.
 * Persisting to storage and stripping the URL happen in an effect once the
 * JWT role is confirmed to be staff.
 */
function readInitialViewAs(): string | null {
  const fromUrl = readViewAsParamFromUrl();
  if (fromUrl) return fromUrl;
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(VIEW_AS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredViewAs(userId: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (userId) window.sessionStorage.setItem(VIEW_AS_STORAGE_KEY, userId);
    else {
      window.sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
      window.sessionStorage.removeItem(VIEW_AS_RECORDED_KEY);
    }
  } catch {
    /* storage unavailable — in-memory state still drives the session */
  }
}

export function ViewAsProvider({ children }: { children: ReactNode }) {
  const { role, loading: authLoading, user } = useAuth();
  const isStaff = isStaffRole(role);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [storedId, setStoredId] = useState<string | null>(readInitialViewAs);
  const [auditRecord, setAuditRecord] = useState<AuditRecordState>({ status: 'idle' });
  const [notice, setNotice] = useState<ViewAsNotice>(null);

  // Non-staff sessions never enter view-as mode, whatever storage or the URL says.
  const viewAsUserId = isStaff ? storedId : null;

  // Consume ?as_user=… (staff only): persist it for the tab and drop it from
  // the URL so it does not stick to every subsequent navigation.
  useEffect(() => {
    if (authLoading || !isStaff) return;
    const param = searchParams.get(VIEW_AS_QUERY_PARAM);
    if (!param) return;
    const next = param.trim();
    if (next) {
      writeStoredViewAs(next);
      if (next !== storedId) {
        setStoredId(next);
        setAuditRecord({ status: 'idle' });
        setNotice(null);
      }
    }
    const remaining = new URLSearchParams(searchParams);
    remaining.delete(VIEW_AS_QUERY_PARAM);
    setSearchParams(remaining, { replace: true });
  }, [authLoading, isStaff, searchParams, setSearchParams, storedId]);

  const {
    data: viewedUser,
    isLoading: viewedUserLoading,
    isSuccess: viewedUserSettled,
  } = useQuery({
    queryKey: ['view-as-user', user?.id, viewAsUserId],
    enabled: isStaff && !!viewAsUserId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ViewedUser | null> => {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, full_name')
        .eq('id', viewAsUserId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const exitViewAs = useCallback(() => {
    writeStoredViewAs(null);
    setStoredId(null);
    setAuditRecord({ status: 'idle' });
    // Every scoped query key carries the viewed user id, but drop the cache
    // anyway so nothing from the previous target lingers in memory.
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'view-as-user' });
    navigate(STAFF_VIEW_AS_PATH, { replace: true });
  }, [navigate, queryClient]);

  // A target that does not resolve to a profile (deleted user, bad link)
  // sends the staff member back to the picker with an explanation.
  useEffect(() => {
    if (!isStaff || !viewAsUserId || !viewedUserSettled || viewedUser) return;
    setNotice('user_not_found');
    writeStoredViewAs(null);
    setStoredId(null);
    setAuditRecord({ status: 'idle' });
    navigate(STAFF_VIEW_AS_PATH, { replace: true });
  }, [isStaff, navigate, viewAsUserId, viewedUser, viewedUserSettled]);

  const setViewAsUser = useCallback(
    (userId: string) => {
      if (!isStaff) return;
      writeStoredViewAs(userId);
      setStoredId(userId);
      setAuditRecord({ status: 'idle' });
      setNotice(null);
    },
    [isStaff],
  );

  const clearNotice = useCallback(() => setNotice(null), []);

  const value = useMemo<ViewAsState>(
    () => ({
      isStaff,
      viewAsUserId,
      viewedUser: viewedUser ?? null,
      viewedUserLoading: isStaff && !!viewAsUserId && viewedUserLoading,
      setViewAsUser,
      exitViewAs,
      auditRecord,
      setAuditRecord,
      notice,
      clearNotice,
    }),
    [
      auditRecord,
      clearNotice,
      exitViewAs,
      isStaff,
      notice,
      setViewAsUser,
      viewAsUserId,
      viewedUser,
      viewedUserLoading,
    ],
  );

  return <ViewAsContext.Provider value={value}>{children}</ViewAsContext.Provider>;
}

export function useViewAs(): ViewAsState {
  const ctx = useContext(ViewAsContext);
  if (!ctx) throw new Error('useViewAs must be used within a ViewAsProvider');
  return ctx;
}
